import { Box, Container, Typography, Divider, Link } from '@mui/material';

const BRAND = 'Fitistic';
const EMAIL = 'info@yuviso.com';
const EFFECTIVE = 'April 17, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 1.5, color: 'text.primary', lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Typography
    component="li"
    variant="body1"
    sx={{ mb: 0.75, color: 'text.primary', lineHeight: 1.8, ml: 2 }}
  >
    {children}
  </Typography>
);

export default function TermsAndConditions() {
  return (
    <Box sx={{ bgcolor: '#F4F4F8', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ color: '#1A1A1A', letterSpacing: '-0.03em', mb: 1 }}
          >
            {BRAND}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
            Terms &amp; Conditions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Effective date: {EFFECTIVE}
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <P>
            Please read these Terms &amp; Conditions ("Terms") carefully before using the{' '}
            {BRAND} mobile application and related services (collectively, the "Service") operated
            by {BRAND} ("we", "us", or "our"). By accessing or using the Service, you agree to be
            bound by these Terms. If you do not agree, do not use the Service.
          </P>

          <Divider sx={{ my: 3 }} />

          <Section title="1. Eligibility">
            <P>
              You must be at least 13 years of age to use the Service. By using the Service, you
              represent and warrant that you meet this requirement. If you are under 18, you
              confirm that you have obtained parental or guardian consent.
            </P>
          </Section>

          <Section title="2. Account Registration">
            <P>
              To access certain features, you must create an account. You agree to:
            </P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>Provide accurate, current, and complete information during registration.</Li>
              <Li>Maintain the security of your password and accept responsibility for all activity under your account.</Li>
              <Li>Notify us immediately at <Link href={`mailto:${EMAIL}`} underline="hover">{EMAIL}</Link> if you suspect unauthorised access.</Li>
              <Li>Not share your account credentials with any third party.</Li>
            </Box>
            <P>
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </P>
          </Section>

          <Section title="3. Use of the Service">
            <P>You agree to use the Service only for lawful purposes. You must not:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>Use the Service in any way that violates applicable local, national, or international law.</Li>
              <Li>Attempt to gain unauthorised access to any part of the Service or its related systems.</Li>
              <Li>Transmit any unsolicited or unauthorised advertising or promotional material.</Li>
              <Li>Reverse-engineer, decompile, or disassemble any part of the Service.</Li>
              <Li>Use automated tools (bots, scrapers) to access or collect data from the Service.</Li>
              <Li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</Li>
            </Box>
          </Section>

          <Section title="4. Health & Fitness Disclaimer">
            <P>
              The Service provides fitness tracking, workout plans, meal plans, and general
              wellness information for informational purposes only. The content is{' '}
              <strong>not a substitute for professional medical advice, diagnosis, or treatment</strong>.
            </P>
            <P>
              Always consult a qualified healthcare provider before starting any new exercise
              programme, diet, or health regimen, especially if you have a pre-existing medical
              condition, injury, or are pregnant. You assume full responsibility for any risks
              associated with your use of the fitness and nutrition features.
            </P>
          </Section>

          <Section title="5. Gym Membership & QR Check-In">
            <P>
              The QR code check-in feature records your attendance at a gym. By scanning a gym's
              QR code, you consent to your check-in time being shared with that gym's
              administrators. Gym membership terms, fees, and access rights are governed solely
              by your agreement with the gym and are not the responsibility of {BRAND}.
            </P>
          </Section>

          <Section title="6. Intellectual Property">
            <P>
              The Service and its original content, features, and functionality are and will
              remain the exclusive property of {BRAND} and its licensors. Our trademarks and
              trade dress may not be used in connection with any product or service without our
              prior written consent.
            </P>
            <P>
              You retain ownership of any personal data and content you submit (workout logs,
              meal entries, etc.). By submitting content, you grant us a limited, non-exclusive
              licence to store and process it solely to provide the Service to you.
            </P>
          </Section>

          <Section title="7. Trainer & Admin Content">
            <P>
              Workout plans, meal plans, and exercises created by gym admins and trainers are
              their intellectual property. {BRAND} provides the platform to publish and distribute
              this content but does not endorse or verify its accuracy. Users follow trainer-created
              plans at their own risk.
            </P>
          </Section>

          <Section title="8. Paid Plans & Payments">
            <P>
              Some workout and meal plans may require payment. All purchases are final unless
              otherwise stated. Pricing is set by the gym or trainer and may change at any time.
              {BRAND} is not responsible for payment disputes between users and gyms or trainers.
            </P>
          </Section>

          <Section title="9. Limitation of Liability">
            <P>
              To the fullest extent permitted by applicable law, {BRAND} shall not be liable for
              any indirect, incidental, special, consequential, or punitive damages, including
              but not limited to loss of data, personal injury, or property damage arising from
              your use of the Service.
            </P>
            <P>
              Our total liability to you for any claim arising from these Terms or the Service
              shall not exceed the amount you paid us in the 12 months preceding the claim, or
              USD $50, whichever is greater.
            </P>
          </Section>

          <Section title="10. Indemnification">
            <P>
              You agree to indemnify and hold harmless {BRAND}, its officers, directors,
              employees, and agents from any claims, damages, losses, liabilities, and expenses
              (including legal fees) arising from your use of the Service, your violation of
              these Terms, or your violation of any third-party rights.
            </P>
          </Section>

          <Section title="11. Termination">
            <P>
              We may suspend or terminate your access to the Service at any time, with or without
              notice, for conduct that we believe violates these Terms or is harmful to other
              users, us, or third parties.
            </P>
            <P>
              You may delete your account at any time by contacting us at{' '}
              <Link href={`mailto:${EMAIL}`} underline="hover">{EMAIL}</Link>. Upon termination,
              your right to use the Service ceases immediately.
            </P>
          </Section>

          <Section title="12. Governing Law">
            <P>
              These Terms shall be governed by and construed in accordance with applicable law.
              Any disputes arising under these Terms shall be subject to the exclusive jurisdiction
              of the competent courts.
            </P>
          </Section>

          <Section title="13. Changes to These Terms">
            <P>
              We reserve the right to modify these Terms at any time. We will notify you of
              material changes by updating the effective date. Your continued use of the Service
              after changes are posted constitutes your acceptance of the revised Terms.
            </P>
          </Section>

          <Section title="14. Contact Us">
            <P>
              If you have any questions about these Terms, please contact us:
            </P>
            <Box
              sx={{
                bgcolor: '#F4F4F8',
                borderRadius: 2,
                p: 2.5,
                mt: 1,
              }}
            >
              <Typography variant="body1" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
                {BRAND}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Email:{' '}
                <Link href={`mailto:${EMAIL}`} underline="hover">
                  {EMAIL}
                </Link>
              </Typography>
            </Box>
          </Section>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © {new Date().getFullYear()} {BRAND}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
